/* eslint-disable */
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate } from 'react-router-dom';
import {
  updateCourseProgress,
  type Course,
  type Chapter,
  getCourseById
} from '../../api/courcesService';
import { startLesson } from '../../api/lessonsService';
import './courseStartPage.css';
import {useStreak} from "../../hooks/useStreak";
import { useTranslation } from 'react-i18next';

const CourseStartPage = () => {
  const { t } = useTranslation();
  const { courseId, lessonId } = useParams();
  const { updateActivity } = useStreak()
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  // const [courseProgress, setCourseProgress] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) {
        setError(t('product.start.errorLoad'));
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // const courseProgressData = await getCourseWithProgress(parseInt(courseId));
        // setCourseProgress(courseProgressData);
        const courseData = await getCourseById(parseInt(courseId));
        setCourse(courseData)
      } catch (err) {
        console.error('Error fetching course data:', err);
        setError(t('product.start.errorLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchCourseData();
  }, [courseId]);

  const findChapterForLesson = (course: Course, lessonId: number): Chapter | null => {
    if (!course.chapters) return null;

    for (const chapter of course.chapters) {
      if (chapter.lessons) {
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (lesson) {
          return chapter;
        }
      }
    }
    return null;
  };

  const handleStartLesson = async () => {
    if (!course || !courseId || !lessonId) {
      console.error('Missing course or lesson data');
      navigate(`/course/${courseId}/lesson/${lessonId}/learn`);
      return;
    }
    try {
      // Find the chapter that contains this lesson
      const chapter = findChapterForLesson(course, parseInt(lessonId));

      if (!chapter) {
        console.error('Chapter not found for lesson');
        navigate(`/course/${courseId}/lesson/${lessonId}/learn`);
        return;
      }
        await updateActivity()

        await startLesson(parseInt(courseId), chapter.id, parseInt(lessonId));

      // Mark the course as started by updating progress
      await updateCourseProgress({
        courseId: parseInt(courseId),
        chapterId: chapter.id,
        lessonId: parseInt(lessonId),
        completed: false,
        progressPercentage: 0,
        timeSpent: 0,
        started: true,
      });

      // Navigate to the learning page
     navigate(`/course/${courseId}/lesson/${lessonId}/learn`);
    } catch (error) {
      console.error('Error updating course progress:', error);
      // Still navigate even if progress update fails
      navigate(`/course/${courseId}/lesson/${lessonId}/learn`);
    }
  };

  const handleClose = () => {
    navigate(`/course/${courseId}`);
  };

  if (loading) {
    return (
      <div className="course-start-container">
        <div className="course-start-loading">{t('product.start.loading')}</div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-start-container">
        <div className="course-start-error">
          {error || t('product.start.notFound')}
          <button onClick={handleClose}>{t('product.start.goBack')}</button>
        </div>
      </div>
    );
  }

  // Find the current lesson from course and lessonId
  const currentLesson = course?.chapters
    ?.flatMap(ch => ch.lessons)
    ?.find(lesson => lesson.id === parseInt(lessonId || '0'));

  return (
    <div className="course-start-container">
      <header className="course-start-header">
        <button className="close-btn" onClick={handleClose} aria-label={t('product.start.closeAria')}>✕</button>
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: '0%' }}></div>
        </div>
      </header>
      <div className="course-start-content">
        <div className="course-start-description">
          <ReactMarkdown
            components={{
              img: ({ node, ...props }) => (
                <img
                  {...props}
                  alt={'lesson'}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    margin: '16px 0',
                    borderRadius: '12px'
                  }}
                />
              ),
              h1: ({ node, ...props }) => (
                <h1 {...props} style={{ fontSize: '1.8rem', fontWeight: 700, margin: '24px 0 16px 0' }} />
              ),
              h2: ({ node, ...props }) => (
                <h2 {...props} style={{ fontSize: '1.5rem', fontWeight: 600, margin: '20px 0 12px 0' }} />
              ),
              h3: ({ node, ...props }) => (
                <h3 {...props} style={{ fontSize: '1.3rem', fontWeight: 600, margin: '16px 0 8px 0' }} />
              ),
              p: ({ node, ...props }) => (
                <p {...props} style={{ marginBottom: '16px', lineHeight: '1.7' }} />
              ),
              ul: ({ node, ...props }) => (
                <ul {...props} style={{ margin: '16px 0', paddingLeft: '24px' }} />
              ),
              ol: ({ node, ...props }) => (
                <ol {...props} style={{ margin: '16px 0', paddingLeft: '24px' }} />
              ),
              li: ({ node, ...props }) => (
                <li {...props} style={{ marginBottom: '8px' }} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote
                  {...props}
                  style={{
                    borderLeft: '4px solid #3b6cff',
                    paddingLeft: '16px',
                    margin: '16px 0',
                    fontStyle: 'italic',
                    color: '#555'
                  }}
                />
              ),
              code: ({ node, ...props }: any) => (
                <code
                  {...props}
                  style={{
                    background: '#f1f3f4',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                    fontSize: '0.9em'
                  }}
                />
              )
            }}
          >
            {currentLesson?.content || "Hi, I'm Alex! Today, we'll explore the fascinating world of ChatGPT — a powerful large language model. If you're new to AI, you've come to the right place."}
          </ReactMarkdown>
        </div>
        <button className="course-start-btn" onClick={handleStartLesson}>{t('product.start.startLesson')}</button>
      </div>
    </div>
  );
};

export default CourseStartPage;
